import { useEffect, useRef } from 'react'
import { useRecoilState } from 'recoil'
import axios from 'axios'
import { facturenStatusState } from '../../../../store/store'

// Polls /api/facturen/status once per second while mounted (i.e. while the
// user is on the "Facturen samenvoegen" tab) and writes the result into
// shared Recoil state. FacturenCard reads that same state to disable itself -
// so this single poller drives both the progress text here AND the disabled
// state on every card, for every user, regardless of who started the run.
const FacturenStatusBar = () => {
    const [status, setStatus] = useRecoilState(facturenStatusState)
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const cancelledRef = useRef(false)

    useEffect(() => {
        cancelledRef.current = false

        const poll = async () => {
            try {
                const response = await axios.get('/api/facturen/status', { timeout: 8000 })
                if (cancelledRef.current) return
                setStatus({
                    inProgress: !!response.data?.inProgress,
                    target: response.data?.target ?? null,
                    totalInvoices: response.data?.totalInvoices ?? 0,
                    processedInvoices: response.data?.processedInvoices ?? 0,
                    lastError: response.data?.lastError ?? null,
                })
            } catch (error) {
                // A single missed poll shouldn't disrupt the UI - the next
                // tick will just try again.
                console.error('Kon factuur-status niet ophalen:', error)
            } finally {
                // Only schedule the NEXT poll after this one has fully
                // resolved (success or failure) - never fire the next poll
                // while one is still in flight. A blind setInterval(poll,
                // 1000) would fire a new request every second regardless of
                // whether the previous one ever came back, which piles up
                // overlapping requests indefinitely if the backend is slow
                // or temporarily down.
                if (!cancelledRef.current) {
                    timeoutRef.current = setTimeout(poll, 1000)
                }
            }
        }

        poll() // immediate first read, don't wait a full second to show state

        return () => {
            cancelledRef.current = true
            if (timeoutRef.current) clearTimeout(timeoutRef.current)
        }
    }, [setStatus])

    if (status.inProgress) {
        const percent = status.totalInvoices > 0
            ? Math.min(100, Math.round((status.processedInvoices / status.totalInvoices) * 100))
            : 0
        return (
            <div style={{
                marginTop: '1em',
                padding: '0.75em 1em',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffe69c',
                borderRadius: '5px',
                color: '#664d03',
            }}>
                <div style={{ fontWeight: 'bold', textAlign: 'center', marginBottom: '0.5em' }}>
                    ⏳ Facturen worden verwerkt: {status.processedInvoices}/{status.totalInvoices}
                </div>
                <div style={{ width: '100%', height: '6px', backgroundColor: '#ffe69c', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ width: `${percent}%`, height: '100%', backgroundColor: '#664d03', transition: 'width 0.5s ease' }} />
                </div>
            </div>
        )
    }

    if (status.lastError) {
        return (
            <div style={{
                marginTop: '1em',
                padding: '0.75em 1em',
                backgroundColor: '#f8d7da',
                border: '1px solid #f1aeb5',
                borderRadius: '5px',
                textAlign: 'center',
                color: '#842029',
                fontWeight: 'bold',
            }}>
                ❌ Er ging iets mis bij het samenvoegen: {status.lastError}
            </div>
        )
    }

    return null
}

export default FacturenStatusBar
